/**
 * Date and Time Utilities for Shipment Tracking
 * 
 * Provides moment.js and moment-timezone functionality with TypeScript type safety.
 * Includes business day calculations and timezone-aware date formatting.
 */

import * as moment from 'moment-timezone';

// Re-export moment for global access
export { moment };

/**
 * Calculate business days (weekdays only) between two dates
 * Excludes weekends (Saturday and Sunday)
 * 
 * @param startDate - Start date (Date object or moment instance)
 * @param endDate - End date (Date object or moment instance)
 * @returns Number of business days, or '-' if count exceeds 1000
 * 
 * @example
 * const shipped = new Date('2025-01-01');
 * const today = new Date();
 * const days = getBusinessDaysDiff(shipped, today); // Returns number of weekdays
 */
export function getBusinessDaysDiff(startDate: Date | moment.Moment, endDate: Date | moment.Moment): number | string {
  let count = 0;
  let currentDate = new Date(startDate instanceof Date ? startDate.getTime() : startDate.toDate().getTime());
  const end = endDate instanceof Date ? endDate : endDate.toDate();

  while (currentDate < end) {
    const dayOfWeek = currentDate.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      // Skip weekends (Sunday = 0, Saturday = 6)
      count++;
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return (count < 1000) ? count : '-';
}

/**
 * Calculate business day difference using moment.js methodology
 * This is the original businessDiff algorithm ported from the legacy code
 * 
 * @param start - Start moment
 * @param end - End moment
 * @returns Number of business days (positive or negative)
 * 
 * @example
 * const shipped = moment.unix(1704067200);
 * const now = moment();
 * const days = businessDiff(shipped, now);
 */
export function businessDiff(start: moment.Moment, end: moment.Moment): number {
  const signal = end.unix() < start.unix() ? 1 : -1;
  const startMoment = moment.min(end, start).clone();
  const endMoment = moment.max(end, start).clone();
  
  let start_offset = startMoment.day() - 7;
  const end_offset = endMoment.day();
  
  const end_sunday = endMoment.clone().subtract(end_offset, 'days');
  const start_sunday = startMoment.clone().subtract(start_offset, 'days');
  const weeks = end_sunday.diff(start_sunday, 'days') / 7;

  start_offset = Math.abs(start_offset);
  start_offset = start_offset === 7 ? 5 : start_offset === 1 ? 0 : start_offset - 2;
  const adjustedEndOffset = end_offset === 6 ? end_offset - 1 : end_offset;

  return Math.round(signal * (weeks * 5 + start_offset + adjustedEndOffset));
}

/**
 * Format a Unix timestamp using moment
 * 
 * @param timestamp - Unix timestamp (seconds)
 * @param format - Moment format string (default: 'MMM D, YYYY h:mm A')
 * @returns Formatted date string
 * 
 * @example
 * formatUnixTimestamp(1704067200); // "Jan 1, 2024 12:00 AM"
 * formatUnixTimestamp(1704067200, 'YYYY-MM-DD'); // "2024-01-01"
 */
export function formatUnixTimestamp(timestamp: number, format: string = 'MMM D, YYYY h:mm A'): string {
  return moment.unix(timestamp).format(format);
}

/**
 * Get relative time from Unix timestamp (e.g., "2 hours ago")
 * 
 * @param timestamp - Unix timestamp (seconds)
 * @returns Humanized relative time string
 * 
 * @example
 * fromNow(Date.now() / 1000 - 3600); // "an hour ago"
 */
export function fromNow(timestamp: number): string {
  return moment.unix(timestamp).fromNow();
}

/**
 * Get current Unix timestamp
 * 
 * @returns Current time as Unix timestamp (seconds)
 * 
 * @example
 * const now = getCurrentUnixTimestamp(); // 1704067200
 */
export function getCurrentUnixTimestamp(): number {
  return moment().unix();
}

/**
 * Get current time in a specific timezone
 * 
 * @param timezone - IANA timezone name (e.g., 'America/Toronto')
 * @returns Moment instance in the specified timezone
 * 
 * @example
 * const torontoTime = getTimeInTimezone('America/Toronto');
 * console.log(torontoTime.format('h:mm A')); // "3:45 PM"
 */
export function getTimeInTimezone(timezone: string): moment.Moment {
  return moment().tz(timezone);
}

/**
 * Format time in a specific timezone
 * 
 * @param timezone - IANA timezone name
 * @param format - Moment format string (default: 'h:mm A z')
 * @returns Formatted time string with timezone
 * 
 * @example
 * formatTimeInTimezone('America/Toronto'); // "3:45 PM EST"
 * formatTimeInTimezone('Europe/London', 'HH:mm z'); // "20:45 GMT"
 */
export function formatTimeInTimezone(timezone: string, format: string = 'h:mm A z'): string {
  return moment().tz(timezone).format(format);
}
